'use client';

import { cn } from '@core/utils';
import Link from 'next/link';
import type { ComponentPropsWithoutRef } from 'react';

import { Image } from '@/components/client';

import { TYPOGRAPHY_IMAGES } from '../constants';
import styles from './StampButton.module.scss';

type StampButtonVariant = 'orange' | 'blue';

type StampButtonProps = {
  variant: StampButtonVariant;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
} & (
  | ({ disabled: true } & ComponentPropsWithoutRef<'button'>)
  | ({ disabled?: false } & ComponentPropsWithoutRef<typeof Link>)
);

const variantStyles: Record<StampButtonVariant, string> = {
  orange:
    'bg-gradient-to-r from-O-400 to-[#FF4800] shadow-[0px_4px_12px_0px_rgba(255,205,161,0.6)]',
  blue: 'bg-gradient-to-r from-B-400 to-B-550 shadow-[0px_4px_12px_0px_rgba(144,213,255,0.6)]',
};

const disabledStyles = 'bg-Gray-300 shadow-none opacity-60 cursor-not-allowed';

export const StampButton = ({
  variant,
  children,
  className,
  disabled = false,
  ...props
}: StampButtonProps) => {
  const getImageByText = (text: React.ReactNode) => {
    if (typeof text !== 'string') return null;

    if (text === '기부하러 가기') {
      return TYPOGRAPHY_IMAGES.buttonDonate;
    }
    if (text === '답례품 더 받기') {
      return TYPOGRAPHY_IMAGES.buttonMoreGifts;
    }
    if (text === '스탬프 모으기 완료!') {
      return TYPOGRAPHY_IMAGES.buttonComplete;
    }

    return null;
  };

  const image = getImageByText(children);

  const baseClassName = cn(
    'flex h-74 w-full items-center justify-center rounded-medium text-center text-Gray-white transition-all duration-150 ease-in-out typo-h4 focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2',
    disabled ? disabledStyles : variantStyles[variant],
    !disabled && 'active:scale-[0.98]',
    !disabled && variant === 'orange' && 'focus-visible:ring-O-300',
    !disabled && variant === 'blue' && 'focus-visible:ring-B-300',
    className,
  );

  const content = image ? (
    <Image
      src={image.url}
      alt={image.alt}
      width={image.width}
      height={image.height}
      priority
      className="object-contain"
    />
  ) : (
    <span style={{ fontFamily: 'Sandoll Hoyoyo' }}>{children}</span>
  );

  return (
    <div
      className={cn(disabled ? 'w-full' : styles.floatingContainer, 'w-full')}
    >
      {disabled ? (
        <button
          type="button"
          className={baseClassName}
          disabled
          {...(props as ComponentPropsWithoutRef<'button'>)}
        >
          {content}
        </button>
      ) : (
        <Link
          className={baseClassName}
          {...(props as ComponentPropsWithoutRef<typeof Link>)}
        >
          {content}
        </Link>
      )}
    </div>
  );
};
